import { WMS_PERMISSIONS } from '@/features/wms/permissions';
import { ACCOUNTING_PERMISSIONS } from '@/features/accounting/permissions';
import { OS_PERMISSIONS } from '@/features/os/permissions';
import { TRANSMITTER_PERMISSIONS } from '@/features/operations-transmitters/permissions';
import { QUOTE_PERMISSIONS } from '@/features/quotes/permissions';
import { INSTALLATION_PERMISSIONS } from '@/features/installations/permissions';
import { ACCOUNT_PERMISSIONS } from '@/features/accounts/permissions';
import { USER_PERMISSIONS } from '@/features/users/permissions';
import { EQUIPMENT_PERMISSIONS } from '@/features/operations-equipment/permissions';

export interface PermissionDefinition {
  key: string;
  label: string;
  description: string;
  level?: 'standard' | 'elevated';
}

export interface PermissionGroup {
  id: string;
  label: string;
  description: string;
  permissions: PermissionDefinition[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'calibration',
    label: 'Calibration',
    description: 'Calibration certificate reporting and actions',
    permissions: [
      {
        key: 'calibrationrecords.read',
        label: 'Read certificates',
        description: 'View calibration certificate lists, summaries, details, and CSV exports.',
      },
      {
        key: 'calibrationrecords.modify',
        label: 'Modify certificates',
        description: 'Run certificate actions such as reminder workflows.',
        level: 'elevated',
      },
      {
        key: 'calibrationrecords.create',
        label: 'Create certificates',
        description: 'Create calibration certificate records in legacy certificate workflows.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'qms',
    label: 'QMS',
    description: 'Quality tickets and approvals',
    permissions: [
      { key: 'qmstickets.read', label: 'Read tickets', description: 'Open QMS and OS Issues boards, ticket details, timelines, and attachments.' },
      { key: 'qmstickets.create', label: 'Create tickets', description: 'Create QMS tickets.' },
      { key: 'qmstickets.edit', label: 'Edit tickets', description: 'Edit QMS ticket details.' },
      { key: 'qmstickets.status', label: 'Change status', description: 'Move QMS tickets between workflow statuses. Approved also requires the separate approval status permission.' },
      { key: 'qmstickets.approve_status', label: 'Set status to approved', description: 'Set QMS ticket status to approved. Also requires Change status permission.', level: 'elevated' },
      { key: 'qmstickets.delete', label: 'Delete tickets', description: 'Delete QMS tickets.', level: 'elevated' },
      { key: 'qmstickets.assign', label: 'Assign tickets', description: 'Assign QMS tickets to users.' },
      { key: 'qmstickets.qd_approval', label: 'QD approval', description: 'Approve QMS items as quality director.', level: 'elevated' },
      { key: 'qmstickets.ceo_review', label: 'CEO review', description: 'Complete CEO review steps.', level: 'elevated' },
      { key: 'qmstickets.change_control', label: 'Change control', description: 'Approve QMS change-control workflows.', level: 'elevated' },
    ],
  },
  {
    id: 'os',
    label: 'OS',
    description: 'EOS pages and operating cadence actions',
    permissions: [
      { key: OS_PERMISSIONS.kpis.read, label: 'Read KPIs', description: 'Open KPI scorecards, definitions, values, and completion statistics.' },
      { key: OS_PERMISSIONS.kpis.create, label: 'Create KPIs', description: 'Create KPI definitions and scorecard values.' },
      { key: OS_PERMISSIONS.kpis.modify, label: 'Modify KPIs', description: 'Edit KPI definitions, reorder scorecards, update values, and restore archived values.' },
      { key: OS_PERMISSIONS.kpis.delete, label: 'Delete KPIs', description: 'Archive KPI definitions and clear or delete KPI values.', level: 'elevated' },
      { key: OS_PERMISSIONS.rocks.read, label: 'Read rocks', description: 'Open rocks, deliverables, comments, and linked items.' },
      { key: OS_PERMISSIONS.rocks.create, label: 'Create rocks', description: 'Create quarterly rocks.' },
      { key: OS_PERMISSIONS.rocks.modify, label: 'Modify rocks', description: 'Edit rocks, statuses, ordering, deliverables, comments, and linked items.' },
      { key: OS_PERMISSIONS.rocks.delete, label: 'Delete rocks', description: 'Archive, unarchive, and delete rocks or rock deliverables.', level: 'elevated' },
      { key: OS_PERMISSIONS.headlines.read, label: 'Read headlines', description: 'Open current, scheduled, and archived headlines.' },
      { key: OS_PERMISSIONS.headlines.create, label: 'Create headlines', description: 'Create team headlines.' },
      { key: OS_PERMISSIONS.headlines.modify, label: 'Modify headlines', description: 'Edit and restore headlines.' },
      { key: OS_PERMISSIONS.headlines.delete, label: 'Delete headlines', description: 'Archive headlines.', level: 'elevated' },
      { key: OS_PERMISSIONS.todos.read, label: 'Read to-dos', description: 'Open EOS to-dos and to-do details.' },
      { key: OS_PERMISSIONS.todos.create, label: 'Create to-dos', description: 'Create EOS to-dos.' },
      { key: OS_PERMISSIONS.todos.modify, label: 'Modify to-dos', description: 'Edit, complete, reopen, and cancel EOS to-dos.' },
      { key: OS_PERMISSIONS.todos.delete, label: 'Delete to-dos', description: 'Delete EOS to-dos.', level: 'elevated' },
      { key: OS_PERMISSIONS.meetings.read, label: 'Read meetings', description: 'Open L10 meetings, agendas, attendees, sections, tickets, and calendar events.' },
      { key: OS_PERMISSIONS.meetings.create, label: 'Create meetings', description: 'Create L10 meetings, agendas, agenda sections, and meeting tickets.' },
      { key: OS_PERMISSIONS.meetings.modify, label: 'Modify meetings', description: 'Edit meetings, agendas, agenda sections, attendees, sections, lifecycle state, and meeting tickets.' },
      { key: OS_PERMISSIONS.meetings.delete, label: 'Delete meetings', description: 'Delete meetings, agendas, agenda sections, and meeting tickets.', level: 'elevated' },
      { key: OS_PERMISSIONS.vto.read, label: 'Read V/TO', description: 'Open V/TO, Playing to Win, Business Model Canvas, and Value Proposition pages.' },
    ],
  },
  {
    id: 'operations-tasks',
    label: 'Operations Tasks',
    description: 'Operational task creation and assignment',
    permissions: [
      { key: 'operations.tasks.create', label: 'Create tasks', description: 'Create operations tasks.' },
      { key: 'operations.tasks.edit', label: 'Edit tasks', description: 'Edit operations task details.' },
      { key: 'operations.tasks.delete', label: 'Delete tasks', description: 'Delete operations tasks.', level: 'elevated' },
      { key: 'operations.tasks.assign', label: 'Assign tasks', description: 'Assign operations tasks to users.' },
    ],
  },
  {
    id: 'operations-projects',
    label: 'Operations Projects',
    description: 'Operational project creation and assignment',
    permissions: [
      { key: 'operations.projects.create', label: 'Create projects', description: 'Create operations projects.' },
      { key: 'operations.projects.edit', label: 'Edit projects', description: 'Edit operations project details.' },
      { key: 'operations.projects.delete', label: 'Delete projects', description: 'Delete operations projects.', level: 'elevated' },
      { key: 'operations.projects.assign', label: 'Assign projects', description: 'Assign operations projects to users.' },
    ],
  },
  {
    id: 'operations-installations',
    label: 'Installation Jobs',
    description: 'Technician installation job creation, maintenance, and IQ review',
    permissions: [
      { key: 'operations.installations.create', label: 'Create jobs', description: 'Create technician installation jobs.' },
      { key: 'operations.installations.edit', label: 'Edit jobs', description: 'Edit technician installation job details.' },
      { key: 'operations.installations.delete', label: 'Delete jobs', description: 'Delete technician installation jobs.', level: 'elevated' },
      {
        key: INSTALLATION_PERMISSIONS.iq,
        label: 'Review IQ',
        description: 'Open the IQ tab to review technician drafts and create installation qualification records.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'operations-equipment',
    label: 'Equipment Library',
    description: 'Fridge, freezer and incubator models, install guides, manuals, and technician submissions',
    permissions: [
      { key: EQUIPMENT_PERMISSIONS.read, label: 'Read equipment library', description: 'Open the Equipment library, install guides, documents, photos, and the review queue.' },
      { key: EQUIPMENT_PERMISSIONS.write, label: 'Edit equipment library', description: 'Create, edit, and archive models; manage aliases, documents, and photos.' },
      {
        key: EQUIPMENT_PERMISSIONS.review,
        label: 'Review submissions',
        description: 'Approve, merge, or reject technician-submitted models and publish their photos.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'operations-transmitters',
    label: 'Operations Transmitters',
    description: 'Transmitter fleet page access and device actions',
    permissions: [
      { key: TRANSMITTER_PERMISSIONS.read, label: 'Read transmitters', description: 'Open the Transmitters page, search the fleet, view details, history, and exports.' },
      { key: TRANSMITTER_PERMISSIONS.create, label: 'Create transmitters', description: 'Create transmitter records when the workflow is available.' },
      { key: TRANSMITTER_PERMISSIONS.edit, label: 'Edit transmitters', description: 'Edit transmitter identity, assignment fields, configuration data, details, and status-related settings.' },
      { key: TRANSMITTER_PERMISSIONS.reassign, label: 'Reassign transmitters', description: 'Move transmitters between customer accounts.', level: 'elevated' },
      { key: TRANSMITTER_PERMISSIONS.manageImages, label: 'Manage transmitter images', description: 'Upload, replace, and remove transmitter images.' },
      { key: TRANSMITTER_PERMISSIONS.pushConfigs, label: 'Push configs', description: 'Queue remote configuration updates for transmitters.', level: 'elevated' },
      { key: TRANSMITTER_PERMISSIONS.pushFirmware, label: 'Push firmware', description: 'Queue firmware OTA updates for transmitters.', level: 'elevated' },
      { key: TRANSMITTER_PERMISSIONS.diagnostics, label: 'View diagnostics', description: 'Open transmitter diagnostics, raw ingress links, gateway history, OTA logs, and config history.' },
      { key: TRANSMITTER_PERMISSIONS.resetSim, label: 'Reset SIM', description: 'Request SIM connection resets for cellular transmitters.', level: 'elevated' },
      { key: TRANSMITTER_PERMISSIONS.delete, label: 'Delete transmitters', description: 'Delete transmitter records.', level: 'elevated' },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'Document management entry points',
    permissions: [
      { key: 'documents.access', label: 'Access documents', description: 'Open the documents area.' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Marketing, outreach, and customer notice pages',
    permissions: [
      {
        key: 'marketing.publication.access',
        label: 'Publication access',
        description: 'Open the publication schedule for campaign content planning.',
      },
      {
        key: 'marketing.brand.access',
        label: 'Brand access',
        description: 'Open the brand guide, logos, personas, and official imagery.',
      },
      {
        key: 'marketing.linkedin.access',
        label: 'LinkedIn access',
        description: 'Open LinkedIn outreach and relationship management tools.',
      },
      {
        key: 'marketing.apollo.access',
        label: 'Apollo access',
        description: 'Open Apollo prospect search and enrichment tools.',
        level: 'elevated',
      },
      {
        key: 'marketing.webcrawler.access',
        label: 'Web crawler access',
        description: 'Open AI-powered web crawling tools for signal discovery.',
        level: 'elevated',
      },
      {
        key: 'marketing.news.access',
        label: 'News access',
        description: 'Open news monitoring for procurement and market signals.',
      },
      {
        key: 'marketing.maintenance_events.access',
        label: 'Maintenance events access',
        description: 'Open customer maintenance notice scheduling tools.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    description: 'Accounting pages and financial actions',
    permissions: [
      {
        key: ACCOUNTING_PERMISSIONS.pnl.read,
        label: 'Read P&L',
        description: 'Open the QuickBooks Profit & Loss dashboard and general-ledger drilldowns.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.pnl.refresh,
        label: 'Refresh P&L',
        description: 'Clear cached QuickBooks Profit & Loss and general-ledger reports.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.invoices.read,
        label: 'Read accounting invoices',
        description: 'Open Accounting invoice lists, ATEK and QuickBooks invoice details, customer lists, and comparison views.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.invoices.create,
        label: 'Create QuickBooks invoices',
        description: 'Create QuickBooks invoices from ATEK invoice records.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.invoices.modify,
        label: 'Modify QuickBooks invoices',
        description: 'Update QuickBooks invoice fields and customer assignments.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.invoices.payment,
        label: 'Record invoice payments',
        description: 'Record QuickBooks payments against invoices.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.invoices.void,
        label: 'Void QuickBooks invoices',
        description: 'Void QuickBooks invoices.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.invoices.attachDocument,
        label: 'Attach invoice documents',
        description: 'Attach DMS documents to QuickBooks invoices.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.reconciliations.read,
        label: 'Read reconciliations',
        description: 'Open bank reconciliation runs and statement-line details.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.reconciliations.create,
        label: 'Create reconciliations',
        description: 'Create reconciliation runs and draft statements.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.reconciliations.modify,
        label: 'Modify reconciliations',
        description: 'Edit reconciliation drafts, lines, and resolution details.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.reconciliations.aiExtract,
        label: 'AI extract statements',
        description: 'Run AI extraction on statement documents.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.reconciliations.start,
        label: 'Start reconciliation workflows',
        description: 'Start bank reconciliation workflows.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.receivable.read,
        label: 'Read receivable',
        description: 'Open accounts receivable aging, invoice drilldowns, and AR backfill reports.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.payable.read,
        label: 'Read payable',
        description: 'Open accounts payable aging reports.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.bills.read,
        label: 'Read bills',
        description: 'Open vendor bill lists, details, documents, and payment records.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.bills.modify,
        label: 'Modify bills',
        description: 'Attach payment proofs to vendor bills.',
        level: 'elevated',
      },
      {
        key: ACCOUNTING_PERMISSIONS.suppliers.read,
        label: 'Read accounting suppliers',
        description: 'Open accounting supplier lists and supplier details.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.suppliers.create,
        label: 'Create accounting suppliers',
        description: 'Create supplier records from the Accounting supplier page.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.suppliers.modify,
        label: 'Modify accounting suppliers',
        description: 'Edit supplier records from the Accounting supplier page.',
      },
      {
        key: ACCOUNTING_PERMISSIONS.suppliers.delete,
        label: 'Delete accounting suppliers',
        description: 'Delete supplier records from the Accounting supplier page.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'wms',
    label: 'WMS',
    description: 'Warehouse pages and operational actions',
    permissions: [
      {
        key: WMS_PERMISSIONS.dashboard.access,
        label: 'Dashboard access',
        description: 'Open the WMS dashboard and summary cards.',
      },
      {
        key: WMS_PERMISSIONS.locations.read,
        label: 'Read locations',
        description: 'Open warehouse locations and zone summaries.',
      },
      {
        key: WMS_PERMISSIONS.locations.create,
        label: 'Create locations',
        description: 'Create warehouse zones and bin locations.',
      },
      {
        key: WMS_PERMISSIONS.locations.modify,
        label: 'Modify locations',
        description: 'Edit warehouse zones, bin locations, and zone photos.',
      },
      {
        key: WMS_PERMISSIONS.locations.delete,
        label: 'Delete locations',
        description: 'Delete warehouse zones and bin locations.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.inventory.read,
        label: 'Read inventory',
        description: 'Open inventory lists, item details, summaries, and movement history.',
      },
      {
        key: WMS_PERMISSIONS.inventory.create,
        label: 'Create inventory',
        description: 'Create inventory records, including manual stock entries.',
      },
      {
        key: WMS_PERMISSIONS.inventory.modify,
        label: 'Modify inventory',
        description: 'Edit inventory item details.',
      },
      {
        key: WMS_PERMISSIONS.inventory.delete,
        label: 'Delete inventory',
        description: 'Delete inventory item records.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.inventory.receive,
        label: 'Receive inventory',
        description: 'Receive stock into inventory.',
      },
      {
        key: WMS_PERMISSIONS.inventory.transfer,
        label: 'Transfer inventory',
        description: 'Move inventory between warehouse locations.',
      },
      {
        key: WMS_PERMISSIONS.inventory.adjust,
        label: 'Adjust inventory',
        description: 'Run cycle-count, correction, and scrap adjustments.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.serials.read,
        label: 'Read serials',
        description: 'Open serialized item lists, details, lookup, and movement history.',
      },
      {
        key: WMS_PERMISSIONS.serials.create,
        label: 'Create serials',
        description: 'Create serialized item records.',
      },
      {
        key: WMS_PERMISSIONS.serials.modify,
        label: 'Modify serials',
        description: 'Edit serialized items and device status.',
      },
      {
        key: WMS_PERMISSIONS.serials.transfer,
        label: 'Transfer serials',
        description: 'Move serialized items between locations.',
      },
      {
        key: WMS_PERMISSIONS.workOrders.read,
        label: 'Read work orders',
        description: 'Open manufacturing work-order lists, details, summaries, items, and serial allocations.',
      },
      {
        key: WMS_PERMISSIONS.workOrders.create,
        label: 'Create work orders',
        description: 'Create work orders directly or from quotes.',
      },
      {
        key: WMS_PERMISSIONS.workOrders.modify,
        label: 'Modify work orders',
        description: 'Edit work orders, line items, serial allocations, and lifecycle state.',
      },
      {
        key: WMS_PERMISSIONS.workOrders.delete,
        label: 'Delete work orders',
        description: 'Delete work orders.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.picking.read,
        label: 'Read picking',
        description: 'Open picking lists, picker workload, details, and summaries.',
      },
      {
        key: WMS_PERMISSIONS.picking.create,
        label: 'Create picking lists',
        description: 'Create picking lists and generate picking from work orders.',
      },
      {
        key: WMS_PERMISSIONS.picking.assign,
        label: 'Assign pickers',
        description: 'Assign picking lists to warehouse users.',
      },
      {
        key: WMS_PERMISSIONS.picking.execute,
        label: 'Execute picking',
        description: 'Start, complete, pick, skip, and report shortages on picking lists.',
      },
      {
        key: WMS_PERMISSIONS.picking.cancel,
        label: 'Cancel picking',
        description: 'Cancel picking lists.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.picking.delete,
        label: 'Delete picking lists',
        description: 'Delete picking lists.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.shipping.read,
        label: 'Read shipping',
        description: 'Open packing slips, details, and shipping summaries.',
      },
      {
        key: WMS_PERMISSIONS.shipping.create,
        label: 'Create packing slips',
        description: 'Create packing slips directly or from work orders.',
      },
      {
        key: WMS_PERMISSIONS.shipping.modify,
        label: 'Modify packing slips',
        description: 'Edit packing slip details, items, and non-shipping lifecycle state.',
      },
      {
        key: WMS_PERMISSIONS.shipping.delete,
        label: 'Delete packing slips',
        description: 'Delete packing slips.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.shipping.ship,
        label: 'Ship packing slips',
        description: 'Prepare, verify, ship, and deliver packing slips.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.procurement.read,
        label: 'Read procurement',
        description: 'Open procurement orders, receiving details, and summaries.',
      },
      {
        key: WMS_PERMISSIONS.procurement.create,
        label: 'Create procurement',
        description: 'Create procurement receiving orders and lines.',
      },
      {
        key: WMS_PERMISSIONS.procurement.modify,
        label: 'Modify procurement',
        description: 'Edit procurement orders and receiving lines.',
      },
      {
        key: WMS_PERMISSIONS.procurement.delete,
        label: 'Delete procurement',
        description: 'Delete procurement orders and receiving lines.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.procurement.receive,
        label: 'Receive procurement',
        description: 'Start, receive, complete, and cancel procurement receiving workflows.',
      },
      {
        key: WMS_PERMISSIONS.procurement.qc,
        label: 'Procurement QC',
        description: 'Update receiving-line quality-control status.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.suppliers.read,
        label: 'Read suppliers',
        description: 'Open supplier lists, supplier details, and supplier selectors.',
      },
      {
        key: WMS_PERMISSIONS.suppliers.create,
        label: 'Create suppliers',
        description: 'Create supplier records.',
      },
      {
        key: WMS_PERMISSIONS.suppliers.modify,
        label: 'Modify suppliers',
        description: 'Edit supplier records.',
      },
      {
        key: WMS_PERMISSIONS.suppliers.delete,
        label: 'Delete suppliers',
        description: 'Delete supplier records.',
        level: 'elevated',
      },
      {
        key: WMS_PERMISSIONS.skus.read,
        label: 'Read SKUs',
        description: 'Open SKU lists, SKU details, SKU search, BOM, suppliers, photos, and document links.',
      },
      {
        key: WMS_PERMISSIONS.skus.create,
        label: 'Create SKUs',
        description: 'Create SKU records.',
      },
      {
        key: WMS_PERMISSIONS.skus.modify,
        label: 'Modify SKUs',
        description: 'Edit SKU records, BOM, suppliers, photos, and SKU document links.',
      },
      {
        key: WMS_PERMISSIONS.skus.delete,
        label: 'Delete SKUs',
        description: 'Delete SKU records.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'quotes',
    label: 'Quotes',
    description: 'Quotes page access and quote actions',
    permissions: [
      { key: QUOTE_PERMISSIONS.read, label: 'Read quotes', description: 'Open the Quotes page, view quote lists, details, stats, and table columns.' },
      { key: QUOTE_PERMISSIONS.create, label: 'Create quotes', description: 'Create new quotes from the Quotes page.' },
      { key: QUOTE_PERMISSIONS.edit, label: 'Edit quotes', description: 'Open the quote edit modal and save changes to quote records, line items, sensors, SKUs, and notes.' },
      { key: QUOTE_PERMISSIONS.delete, label: 'Delete quotes', description: 'Delete quote records.', level: 'elevated' },
      { key: QUOTE_PERMISSIONS.print, label: 'Print quotes', description: 'Render and print quote PDFs from the Quotes table.' },
      { key: QUOTE_PERMISSIONS.export, label: 'Export quotes', description: 'Copy quotes to clipboard and download CSV exports.' },
      { key: QUOTE_PERMISSIONS.viewLinks, label: 'View quote links', description: 'Open HubSpot and Google Drive links attached to quotes.' },
    ],
  },
  {
    id: 'accounts',
    label: 'Accounts',
    description: 'Customer account creation',
    permissions: [
      {
        key: ACCOUNT_PERMISSIONS.create,
        label: 'Create accounts',
        description: 'Create new customer accounts from the Accounts page.',
        level: 'elevated',
      },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    description: 'Platform user creation',
    permissions: [
      {
        key: USER_PERMISSIONS.create,
        label: 'Create users',
        description: "Create new users from an account's Users tab.",
        level: 'elevated',
      },
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    description: 'Orders, invoices, and subscriptions',
    permissions: [
      { key: 'orders.read', label: 'Read orders', description: 'View orders.' },
      { key: 'orders.create', label: 'Create orders', description: 'Create orders.' },
      { key: 'orders.modify', label: 'Modify orders', description: 'Edit order records.' },
      { key: 'orders.delete', label: 'Delete orders', description: 'Delete order records.', level: 'elevated' },
      { key: 'invoices.read', label: 'Read invoices', description: 'View invoices.' },
      { key: 'invoices.create', label: 'Create invoices', description: 'Create invoices.' },
      { key: 'invoices.modify', label: 'Modify invoices', description: 'Edit invoice records.' },
      { key: 'subscriptions.read', label: 'Read subscriptions', description: 'View subscriptions.' },
      { key: 'subscriptions.create', label: 'Create subscriptions', description: 'Create subscriptions.' },
      { key: 'subscriptions.modify', label: 'Modify subscriptions', description: 'Edit subscription records.' },
      { key: 'subscriptions.delete', label: 'Delete subscriptions', description: 'Delete subscription records.', level: 'elevated' },
    ],
  },
  {
    id: 'roles',
    label: 'Roles',
    description: 'Legacy role records and role table visibility',
    permissions: [
      { key: 'roles.read', label: 'Read roles', description: 'View role records.' },
      { key: 'roles.create', label: 'Create roles', description: 'Create role records.', level: 'elevated' },
      { key: 'roles.modify', label: 'Modify roles', description: 'Edit role records.', level: 'elevated' },
      { key: 'roles.delete', label: 'Delete roles', description: 'Delete role records.', level: 'elevated' },
      { key: 'roles.read.permissions', label: 'Read role permissions column', description: 'Show permission details in the roles table.' },
      { key: 'roles.read.created_by', label: 'Read role creator column', description: 'Show creator details in the roles table.' },
      { key: 'roles.read.created_at', label: 'Read role created date column', description: 'Show creation dates in the roles table.' },
    ],
  },
];

export const KNOWN_PERMISSION_KEYS = new Set(
  PERMISSION_GROUPS.flatMap((group) => group.permissions.map((permission) => permission.key)),
);
